import {
  AI_RADIUS_PER_LEVEL,
  GROWTH_PER_LEVEL,
  NEAR_MISS_RADIUS_MULT,
  PLAYER_BASE_RADIUS,
  PLAYER_RADIUS_STEP,
} from './configs';
export interface CircleBody {
  x: number;
  y: number;
  radius: number;
}
export interface FishState {
  level: number;
  growth: number;
}
export interface GrowthResult {
  level: number;
  growth: number;
  radius: number;
}
export type CollisionResult = 'eat' | 'die' | 'bounce' | 'none';
export const radiusForLevel = (level: number): number => {
  const safeLevel = Math.max(1, Math.floor(level));
  return PLAYER_BASE_RADIUS + (safeLevel - 1) * PLAYER_RADIUS_STEP;
};
export const radiusForFishLevel = (level: number): number =>
  Math.max(9, Math.floor(level) * AI_RADIUS_PER_LEVEL);
const circlesOverlap = (a: CircleBody, b: CircleBody): boolean => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const combined = a.radius + b.radius;
  return dx * dx + dy * dy <= combined * combined;
};
export const resolveCollision = (
  playerLevel: number,
  playerAlive: boolean,
  player: CircleBody,
  fishLevel: number,
  fish: CircleBody,
): CollisionResult => {
  if (!playerAlive || !circlesOverlap(player, fish)) return 'none';
  if (fishLevel < playerLevel) return 'eat';
  if (fishLevel > playerLevel) return 'die';
  return 'bounce';
};
export const resolveDashCollision = (
  collision: CollisionResult,
  dashTimeRemaining: number,
): CollisionResult => {
  if (!Number.isFinite(dashTimeRemaining) || dashTimeRemaining <= 0) return collision;
  if (collision === 'die') return 'none';
  return collision;
};
export const isNearMiss = (player: CircleBody, largerFish: CircleBody): boolean => {
  if (player.radius < 0 || largerFish.radius < 0) return false;
  const dx = player.x - largerFish.x;
  const dy = player.y - largerFish.y;
  const distanceSquared = dx * dx + dy * dy;
  const threshold = (player.radius + largerFish.radius) * NEAR_MISS_RADIUS_MULT;
  return distanceSquared <= threshold * threshold;
};
export const applyGrowth = (state: FishState): GrowthResult => {
  const level = Math.max(1, Math.floor(state.level));
  const growth = Math.max(0, Math.floor(state.growth)) + 1;
  const leveled = growth >= GROWTH_PER_LEVEL;
  const nextLevel = level + (leveled ? 1 : 0);
  const nextGrowth = leveled ? growth - GROWTH_PER_LEVEL : growth;
  return { level: nextLevel, growth: nextGrowth, radius: radiusForLevel(nextLevel) };
};
export const pickFishLevel = (
  playerLevel: number,
  roll: number,
): { level: number; kind: 'prey' | 'predator' } => {
  const safeLevel = Math.max(1, Math.floor(playerLevel));
  if (roll < 0.62) return { level: Math.max(0, safeLevel - 1), kind: 'prey' };
  if (roll < 0.84) return { level: safeLevel, kind: 'prey' };
  if (roll < 0.97) return { level: safeLevel + 2, kind: 'predator' };
  return { level: safeLevel + 3, kind: 'predator' };
};
export const clampPosition = (
  position: CircleBody,
  width: number,
  height: number,
): CircleBody => {
  const radius = Math.max(0, position.radius);
  const safeWidth = Math.max(radius * 2, width);
  const safeHeight = Math.max(radius * 2, height);
  return {
    ...position,
    x: Math.min(safeWidth - radius, Math.max(radius, position.x)),
    y: Math.min(safeHeight - radius, Math.max(radius, position.y)),
  };
};
export const separateEqualFish = (player: CircleBody, fish: CircleBody): CircleBody => {
  let dx = fish.x - player.x;
  let dy = fish.y - player.y;
  let distance = Math.hypot(dx, dy);
  if (distance < 0.001) {
    dx = 1;
    dy = 0;
    distance = 1;
  }
  const targetDistance = player.radius + fish.radius + 2;
  if (distance >= targetDistance) return { ...fish };
  return {
    ...fish,
    x: player.x + (dx / distance) * targetDistance,
    y: player.y + (dy / distance) * targetDistance,
  };
};
export const createMulberry32 = (seed: number): (() => number) => {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
};
